import { inject } from '@angular/core';
import { CanActivateFn, RouterStateSnapshot } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';

import { AuthSessionService } from '@core/auth/services/auth-session.service';
import { ImpersonateUserConfigService } from '@core/services/impersonate-user-config.service';
import { UserDetailsService } from '@core/services/user-details.service';

export const oktaAuthGuard: CanActivateFn = async (_route, state: RouterStateSnapshot) => {
  const oktaAuth = inject(OKTA_AUTH) as OktaAuth;
  const authSession = inject(AuthSessionService);
  const mainConfig = inject(ImpersonateUserConfigService);
  const userDetails = inject(UserDetailsService);

  const isSessionActive = await oktaAuth.isAuthenticated();

  if (isSessionActive) {
    // The Okta SSO redirect finishes token exchange after app.config's initializer already ran,
    // so persona-dependent routes (e.g. the default dashboard/action redirect) need this ensured here.
    if (!authSession.personaDetails()) {
      try {
        const effectiveEmail = await authSession.getUserEmail();
        if (effectiveEmail.trim()) {
          await Promise.all([
            mainConfig.loadMainConfig(),
            userDetails.loadUserContextByEmail(effectiveEmail),
          ]);
        }
      } catch {
        // Route activation proceeds even if persona context fails to load; access checks fail closed.
      }
    }

    return true;
  }

  await oktaAuth.signInWithRedirect({
    originalUri: state.url || '/dashboard',
  });

  return false;
};
